import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box, Cpu, Layers, Gauge, ArrowRight, CheckCircle2 } from 'lucide-react';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import LightPageHero from '@/components/LightPageHero';

const processSteps = [
  {
    step: '01',
    title: 'Briefing & Design',
    desc: 'We discuss requirements and dimensions. We then create or optimise the CAD model using AI-assisted tools.',
  },
  {
    step: '02',
    title: 'Material Selection',
    desc: 'PLA, PETG, ABS, TPU or specialist filament — we choose the material based on function and environment.',
  },
  {
    step: '03',
    title: 'Printing & QC',
    desc: 'High-precision printing with thorough quality control. Tolerances down to ±0.1 mm.',
  },
  {
    step: '04',
    title: 'Post-processing',
    desc: 'Sanding, painting, assembly or surface treatment — ready for delivery to your door.',
  },
];

const features = [
  {
    icon: Gauge,
    title: 'Fast turnaround',
    desc: 'Prototypes within 24–72 hours from approved design. No long waiting times.',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.3)',
  },
  {
    icon: Layers,
    title: 'All materials',
    desc: 'PLA, PETG, ABS, ASA, TPU, CF-Nylon and more. The right material for the right application.',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.3)',
  },
  {
    icon: Cpu,
    title: 'AI-assisted CAD',
    desc: 'We use AI to optimise designs for strength, weight and material consumption.',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.3)',
  },
  {
    icon: Box,
    title: 'Small & large runs',
    desc: 'From single prototypes to runs of hundreds of units — the same precision throughout.',
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.3)',
  },
];

const useCases = [
  'Product prototypes & proof-of-concept',
  'Machine parts & spare parts',
  'Jigs, fixtures & assembly aids',
  'Consumer products & housings',
  'Architectural models & mockups',
  'Custom packaging & inserts',
  'Custom replacement parts & components',
  'Workshop-specific fixtures and templates',
];

const En3DPrintingPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CorporateNavbar />

      <LightPageHero
        badge="3D PRINTING · 24–72H DELIVERY"
        heading={<>Get your product produced<br /><span style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #60a5fa 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>within 24–72 hours</span></>}
        subtext="Prototypes, spare parts and production components. Fast, precise and without middlemen."
        ctaLabel="Request a quote"
        ctaHref="/en/contact"
      />

      {/* ── Features ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Why Aifinity</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              Precision meets speed
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <motion.div
                key={f.title}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-white rounded-2xl border border-border-light p-6 transition-all duration-300"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 24px rgba(99,102,241,0.15), 0 20px 50px ${f.glow}`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)'; }}
              >
                <div className={`inline-flex w-10 h-10 rounded-xl bg-gradient-to-br ${f.gradient} items-center justify-center mb-4 shadow-md`}
                  style={{ boxShadow: `0 4px 12px ${f.glow}` }}>
                  <f.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-[17px] font-bold text-text-primary mb-2">{f.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-24 bg-surface-subtle">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">Our process</span>
            <h2 className="text-3xl sm:text-[40px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight">
              How we work
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((p) => (
              <div
                key={p.step}
                className="bg-white rounded-2xl p-6 border border-border-light"
                style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.08), 0 0 0 1px rgba(99,102,241,0.06)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-4 shadow-md">
                  <span className="text-xs font-bold text-white">{p.step}</span>
                </div>
                <h3 className="text-[17px] font-bold text-text-primary mb-2">{p.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases + CTA ── */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">

            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-5">Use cases</span>
              <h2 className="text-3xl font-extrabold text-text-primary tracking-tight mb-8">
                What we can print for you
              </h2>
              <ul className="space-y-3.5">
                {useCases.map(u => (
                  <li key={u} className="flex items-start gap-3 text-[15px] text-text-secondary">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    {u}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="bg-surface-subtle rounded-3xl p-10 border border-border-light"
            >
              <h3 className="text-2xl font-extrabold text-text-primary mb-4">Ready to order?</h3>
              <p className="text-text-secondary leading-relaxed mb-6">
                Send us your files or tell us about your project — we'll put together a quote and get back to you within 24 hours.
              </p>
              <Link to="/en/contact">
                <button className="group btn-glow relative overflow-hidden w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 text-white font-semibold px-7 py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
                  Send a request
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </button>
              </Link>
              <p className="text-xs text-text-secondary mt-4 text-center">Free quote · Fast delivery · Professional result</p>
            </motion.div>

          </div>
        </div>
      </section>

      <CorporateFooter />
    </div>
  );
};

export default En3DPrintingPage;
